import React, { useMemo } from 'react';
import { ArrowLeft, ArrowRight, Briefcase, Building2, Check, CheckCircle2, Clock, Layers, Palette, X } from 'lucide-react';

export type SiteConfigTab =
  | 'business-info'
  | 'services-categories'
  | 'operating-hours'
  | 'branding-theme'
  | 'payments'
  | 'cancellation-rescheduling-policy';

type GuidedStep = {
  title: string;
  description: string;
  ctaLabel: string;
  action:
    | { type: 'site-config-tab'; tab: SiteConfigTab }
    | { type: 'services-management' };
  icon: React.ReactNode;
};

export const GuidedSetupModal: React.FC<{
  businessName?: string;
  onClose: () => void;
  onOpenSiteConfigTab: (tab: SiteConfigTab) => void;
  onOpenServicesManagement: () => void;
  onDone: () => void;
  stepIndex: number;
  onStepIndexChange: (nextIndex: number) => void;
  onSaveSiteConfig?: () => Promise<void>;
}> = ({
  businessName,
  onClose,
  onOpenSiteConfigTab,
  onOpenServicesManagement,
  onDone,
  stepIndex,
  onStepIndexChange,
  onSaveSiteConfig
}) => {
  const steps: GuidedStep[] = useMemo(
    () => [
      {
        title: 'Business Information',
        description:
          'Add the basics customers will see: business name, description, contact details, and key info used across your booking experience.',
        ctaLabel: 'Open Business Information',
        action: { type: 'site-config-tab', tab: 'business-info' },
        icon: <Building2 className="h-5 w-5 text-[#0B4778]" />
      },
      {
        title: 'Services & Categories',
        description:
          'Configure where services are available (in-store, on-site, etc.) and categorize your business so customers can find you in search.',
        ctaLabel: 'Open Services & Categories',
        action: { type: 'site-config-tab', tab: 'services-categories' },
        icon: <Layers className="h-5 w-5 text-[#0B4778]" />
      },
      {
        title: 'Operating Hours',
        description:
          'Set your open/close times so booking availability reflects your real schedule and customers see accurate hours.',
        ctaLabel: 'Open Operating Hours',
        action: { type: 'site-config-tab', tab: 'operating-hours' },
        icon: <Clock className="h-5 w-5 text-[#0B4778]" />
      },
      {
        title: 'Branding and Themes',
        description:
          'Upload your logo, pick theme colors, and choose a look that matches your brand across your website and booking pages.',
        ctaLabel: 'Open Branding & Themes',
        action: { type: 'site-config-tab', tab: 'branding-theme' },
        icon: <Palette className="h-5 w-5 text-[#0B4778]" />
      },
      {
        title: 'Service Management',
        description:
          'Create and maintain your service menu (pricing, duration, add-ons, and active/inactive services) so customers can book the right appointment.',
        ctaLabel: 'Open Services Management',
        action: { type: 'services-management' },
        icon: <Briefcase className="h-5 w-5 text-[#0B4778]" />
      }
    ],
    []
  );

  const stepIdx = Math.min(Math.max(stepIndex, 0), steps.length - 1);
  const step = steps[stepIdx];
  const progressPct = Math.round(((stepIdx + 1) / steps.length) * 100);

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full sm:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]">
          {/* Header */}
          <div className="p-5 sm:p-6 border-b border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Guided Setup</h2>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Welcome{businessName ? ` to ${businessName}` : ''}. Let’s get your site ready for customers.
                </p>
              </div>
              <button
                onClick={onClose}
                className="shrink-0 rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close guided setup"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Step {stepIdx + 1} of {steps.length}
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="mt-2 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-[#0B4778] rounded-full transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-6 overflow-auto">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-10 w-10 rounded-xl bg-[#0B4778]/10 flex items-center justify-center">
                {step.icon}
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">{step.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{step.description}</p>
              </div>
            </div>

            <button
              onClick={() => {
                if (step.action.type === 'site-config-tab') {
                  onOpenSiteConfigTab(step.action.tab);
                } else {
                  onOpenServicesManagement();
                }
                onClose();
              }}
              className="mt-5 w-full aacore-btn2-primary inline-flex items-center justify-center gap-2"
            >
              {step.ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Footer */}
          <div className="p-5 sm:p-6 border-t border-gray-200 flex items-center justify-between gap-3">
            <button
              onClick={() => onStepIndexChange(Math.max(0, stepIdx - 1))}
              disabled={stepIdx === 0}
              className="aacore-btn2-outline inline-flex items-center gap-2 disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="flex items-center gap-2">
              <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-800">
                Skip for now
              </button>
              {stepIdx === steps.length - 1 ? (
                <button
                  onClick={() => {
                    onDone();
                    onOpenSiteConfigTab('business-info');
                    onClose();
                  }}
                  className="aacore-btn2-primary inline-flex items-center gap-2"
                >
                  Done
                  <Check className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={async () => {
                    // Steps 1-5 (0-4 in 0-indexed) are all Site Configuration tabs
                    // Auto-save before advancing to prevent data loss
                    if (stepIdx < 5 && onSaveSiteConfig) {
                      try {
                        await onSaveSiteConfig();
                      } catch (error) {
                        // Silently handle save errors - don't block navigation
                        // The user can still manually save if needed
                        console.error('Auto-save failed during guided setup:', error);
                      }
                    }
                    onStepIndexChange(Math.min(steps.length - 1, stepIdx + 1));
                  }}
                  className="aacore-btn2-primary inline-flex items-center gap-2"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

